import { useCallback, useEffect, useState } from "react";

export type PositionsTableDensity = "condensed" | "normal" | "expanded";

export interface PositionsTableDisplay {
  density: PositionsTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
}

export const POSITIONS_TABLE_DISPLAY_STORAGE_KEY =
  "atd:positions:table-display:v1";

const DEFAULT_DISPLAY: PositionsTableDisplay = {
  density: "normal",
  horizontalLines: true,
  verticalLines: false,
};

export interface PositionsTableDisplayStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

const getStorage = (): PositionsTableDisplayStorage | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const isDensity = (value: unknown): value is PositionsTableDensity =>
  value === "condensed" || value === "normal" || value === "expanded";

export const sanitizePositionsTableDisplay = (
  value: unknown,
): PositionsTableDisplay => {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_DISPLAY;
  }
  const record = value as Record<string, unknown>;
  return {
    density: isDensity(record.density)
      ? record.density
      : DEFAULT_DISPLAY.density,
    horizontalLines:
      typeof record.horizontalLines === "boolean"
        ? record.horizontalLines
        : DEFAULT_DISPLAY.horizontalLines,
    verticalLines:
      typeof record.verticalLines === "boolean"
        ? record.verticalLines
        : DEFAULT_DISPLAY.verticalLines,
  };
};

export const loadPositionsTableDisplay = (
  storage: PositionsTableDisplayStorage | null = getStorage(),
): PositionsTableDisplay => {
  if (!storage) {
    return DEFAULT_DISPLAY;
  }
  try {
    const raw = storage.getItem(POSITIONS_TABLE_DISPLAY_STORAGE_KEY);
    return raw === null
      ? DEFAULT_DISPLAY
      : sanitizePositionsTableDisplay(JSON.parse(raw));
  } catch {
    return DEFAULT_DISPLAY;
  }
};

export const persistPositionsTableDisplay = (
  display: PositionsTableDisplay,
  storage: PositionsTableDisplayStorage | null = getStorage(),
): boolean => {
  if (!storage) {
    return false;
  }
  try {
    storage.setItem(
      POSITIONS_TABLE_DISPLAY_STORAGE_KEY,
      JSON.stringify(sanitizePositionsTableDisplay(display)),
    );
    return true;
  } catch {
    return false;
  }
};

export const usePositionsTableDisplay = (
  storage: PositionsTableDisplayStorage | null = getStorage(),
) => {
  const [display, setDisplay] = useState<PositionsTableDisplay>(() =>
    loadPositionsTableDisplay(storage),
  );

  useEffect(() => {
    persistPositionsTableDisplay(display, storage);
  }, [display, storage]);

  const setDensity = useCallback((density: PositionsTableDensity) => {
    setDisplay((current) => ({ ...current, density }));
  }, []);

  const setHorizontalLines = useCallback((horizontalLines: boolean) => {
    setDisplay((current) => ({ ...current, horizontalLines }));
  }, []);

  const setVerticalLines = useCallback((verticalLines: boolean) => {
    setDisplay((current) => ({ ...current, verticalLines }));
  }, []);

  return {
    ...display,
    setDensity,
    setHorizontalLines,
    setVerticalLines,
  };
};
